<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Welcome to your acting work experience induction! Through this work experience, you will
        gain valuable insights into the exciting world of the arts and entertainment industry.
      </p>
      <p class="mb-5">
        A big part of work experience is coming in with a great attitude and lots of enthusiasm as
        well as a place for you to learn lots of new things.
      </p>
      <p>
        Being an actor is a fun, interesting and varied career path. But before we jump in with the
        fun stuff, below are some of the things that will be expected of you and what will be
        covered during your work experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-5">
      <h4 class="mb-5">Let’s start your virtual job experience!</h4>
      <p class="mb-5">
        Acting is a type of performing art that uses movement, gesture, and intonation to bring a
        fictional character to life for either theatre, film, or TV.
      </p>
      <p class="mb-5">
        If you think of your favourite film and why you love it, it’s probably because it makes you
        feel happy, makes you laugh or even cry! That’s what acting is all about - making the
        audience think and feel. Take a look at the video for an example of making the audience
        laugh and feel good.
      </p>
      <p class="mb-5">https://www.youtube.com/watch?v=k1oXx4delIY</p>

      <p class="mb-5">
        Actors usually interpret a writer’s work - for example a script - with instruction and
        support from the director, although some directors might ask the actor to improvise.
      </p>
      <p class="mb-5">
        There may be times when acting work is easier to find, yet at other times, work may be
        scarce and actors may be out of work for some time. This means they may not be able to count
        on a regular income from acting straight away.
      </p>
      <p class="mb-5">
        To be an ‘actor’ is a general way of saying that you act for a living - but that has lots of
        sub-categories, meaning there are many different types of acting jobs and opportunities. Not
        all actors are movie stars - so Let’s check out some of the acting careers you can pursue!
      </p>

      <p class="mb-5">What types of acting are there?</p>
      <ul class="default-list">
        <li>Film acting</li>
        <li>TV acting</li>
        <li>Theatre (stage) acting</li>
        <li>Voice over acting</li>
        <li>Commercial acting</li>
        <li>Extra / background acting</li>
        <li>Dramatisation acting</li>
      </ul>

      <p class="mb-5">What are some helpful traits for acting?</p>
      <ul class="default-list">
        <li>Determination</li>
        <li>Commitment</li>
        <li>Self-confidence</li>
        <li>Patience</li>
        <li>Clear speaking skills</li>
        <li>Ability to cope with changing situations</li>
      </ul>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">What do you need to do to become an actor?</h4>
      <p class="mb-5">
        To become an actor, one thing you will need is a high level of ‘performance skill’ - this
        means an ability to actually act and perform!
      </p>
      <p class="mb-5">
        Don’t worry if you are not sure where your skill level is at, as there are lots of ways you
        can improve along the way, like acting classes or youtube tutorials.
      </p>
      <p class="mb-5">
        The good news is, formal qualifications are not essential. This means you could film
        yourself for an audition and get the part simply for being naturally great at performing.
      </p>
      <p class="mb-5">
        On the other hand, university and Vocational Education and Training (VET) are both common
        study pathways and are advisable to help you understand and learn more about acting, and
        improve your chances of gaining employment.
      </p>
      <p class="mb-5">
        Course competition can be strong, especially at university, which means an audition,
        practical test or interview may be required. However, different institutions have different
        requirements and some are more flexible than others - so there are always options.
      </p>

      <h4 class="mb-5">VET courses include:</h4>
      <ul class="default-list">
        <li>Acting</li>
        <li>Performing arts</li>
        <li>Music theatre</li>
        <li>Theatre and screen performance</li>
      </ul>

      <h4 class="mb-5">Bachelor’s degrees include:</h4>
      <ul class="default-list">
        <li>Acting</li>
        <li>Drama</li>
        <li>Performing arts</li>
        <li>Music theatre</li>
        <li>Theatre studies</li>
      </ul>

      <h4 class="mb-5">Top tips:</h4>
      <ul class="default-list">
        <li>Consider hiring a talent agent or management agency</li>
        <li>Think about joining a local theatre</li>
      </ul>
    </vx-card>
    <vx-card class="mt-base mb-5">
      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
          <h4 class="mb-5">What tasks and responsibilities do actors have?</h4>
          <p class="mb-5">
            Here are some tasks actors are expected to do, and what being an actor really involves!
          </p>

          <ul class="default-list">
            <li>
              Reading scripts and undertaking research to gain understanding of themes and
              characterisations
            </li>
            <li>
              Learning lines and cues, rehearsing parts, and applying vocal and movement skills to
              the development of characterisation
            </li>
            <li>
              Preparing for performances through rehearsals under the instruction and guidance of
              production directors
            </li>
            <li>
              Acting parts and portraying roles as developed in rehearsals in film, television,
              radio and stage productions
            </li>
            <li>
              Practising dance routines and interpreting the choreographic content of the production
            </li>
            <li>
              Performing dances for audience entertainment, coordinating body movements and facial
              expression, usually with musical accompaniment
            </li>
            <li>Composing and notating ballet compositions and other dance routines</li>
            <li>Creating and performing individual performance routines</li>
            <li>Rehearsing, auditioning and travelling between entertainment venues</li>
          </ul>
          <p class="mb-5">Source: joboutlook.gov.au</p>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
          <img
            src="@/assets/images/experiences/actor/jo1.png"
            class="mx-auto img-responsive"
            alt="Actors, Dancers and Other Entertainers job outlook: $1,366 weekly pay, moderate future growth. Actors: 1,500 workers, very high skill level, 35% full-time, 44 hours average full-time week, average age 37, 46% female."
          />
        </div>
      </div>
      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">Highlights of becoming an actor</h4>
      <h5 class="mb-5">1. Variety of work</h5>
      <p class="mb-5">
        Compared to other careers, actors work in a variety of different environments and get to do
        different things. No two days would look the same - so if you like the idea of variety in
        your career, look no further! Actors who primarily work on stage also get to work in
        interesting places, such as historical sites and classic theaters.
      </p>
      <h5 class="mb-5">2. Being in the spotlight</h5>
      <p class="mb-5">
        If you love the idea of people paying attention to you while performing, acting might be
        perfect for you. Some love the fame and fortune attached to acting, but it can be
        intimidating to others. If you love the idea of the spotlight being on you, acting can be a
        way of achieving that.
      </p>

      <h5 class="mb-5">3. Meeting new people</h5>
      <p class="mb-5">
        Actors typically work with groups of people on each project, including directors, producers,
        costumers and other actors. Because of this, actors spend much of their time collaborating
        with others. This can be a large pro for people who enjoy meeting new people!
      </p>
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      image: require('@/assets/images/experiences/experience-one.png'),
      image1: require('@/assets/images/experiences/doctor/jo1.png'),
      image2: require('@/assets/images/experiences/doctor/jo2.png'),
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      showVideoPopup: false,
      videoTriggerImg: require('@/assets/images/experiences/nurse/1. Male Nurse.png'),
    };
  },
  provide: function () {
    return {
      openPopup: this.openPopup,
      closePopup: this.closePopup,
    };
  },
  methods: {
    openPopup() {
      this.showVideoPopup = true;
    },
    closePopup() {
      this.showVideoPopup = false;
    },
  },
};
</script>
